import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { CalendarClock, CalendarDays, Columns3, List, ListPlus, Pencil, Plus } from "lucide-react";
import { z } from "zod";
import {
  AppointmentStatusSchema,
  AppointmentTransportTypeSchema,
  AppointmentTypeSchema,
  TaskReasonSchema,
  TaskTypeSchema,
  VinSchema,
  utcToWallTime,
  wallClockDate,
  wallTimeToUtc,
  type Appointment,
  type Task,
  type UpdateAppointmentDto,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Calendar,
  type CalendarEvent,
  type CalendarEventTone,
  type CalendarView,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import {
  formatClockTimeInZone,
  formatDateTime,
  formatDateTimeInZone,
  humanize,
} from "../../lib/format";

/**
 * Appointment times on this page are STORE times — rendered and edited
 * in the appointment rooftop's IANA zone (fallback: the dealership's
 * primary timezone), never the admin's browser zone. Task due times
 * stay browser-local (they're operator to-dos, not store visits).
 */
type ZoneFor = (rooftopId: string | null | undefined) => string;

const BROWSER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;
type AppointmentType = z.infer<typeof AppointmentTypeSchema>;
type TransportType = z.infer<typeof AppointmentTransportTypeSchema>;
type TaskType = z.infer<typeof TaskTypeSchema>;
type TaskReason = z.infer<typeof TaskReasonSchema>;

type ViewMode = "calendar" | "list" | "board";
const VIEW_MODES: readonly ViewMode[] = ["calendar", "list", "board"];

function isViewMode(value: string | null): value is ViewMode {
  return value !== null && (VIEW_MODES as readonly string[]).includes(value);
}

const STATUS_VARIANT: Record<
  AppointmentStatus,
  "default" | "success" | "warning" | "secondary" | "destructive"
> = {
  unconfirmed: "warning",
  pending_customer: "warning",
  confirmed: "default",
  checked_in: "default",
  in_service: "default",
  ready_for_pickup: "success",
  completed: "success",
  cancelled: "secondary",
  no_show: "destructive",
  rescheduled: "warning",
};

const STATUS_TONE: Record<AppointmentStatus, CalendarEventTone> = {
  unconfirmed: "warning",
  pending_customer: "warning",
  confirmed: "default",
  checked_in: "default",
  in_service: "default",
  ready_for_pickup: "success",
  completed: "success",
  cancelled: "muted",
  no_show: "danger",
  rescheduled: "warning",
};

function appointmentToEvent(
  a: Appointment,
  contactName: Map<string, string>,
  zoneFor: ZoneFor,
): CalendarEvent {
  // The grid lays events out by local Date components, so feed it
  // dates SHIFTED to the store's wall clock — the calendar then shows
  // store time for every viewer regardless of browser zone.
  const zone = zoneFor(a.rooftopId);
  const start = wallClockDate(a.scheduledStartAt, zone);
  const instant = new Date(a.scheduledStartAt);
  return {
    id: a.id,
    start,
    end: wallClockDate(
      new Date(instant.getTime() + a.durationMinutes * 60_000),
      zone,
    ),
    title: contactName.get(a.contactId) ?? "Appointment",
    subtitle: humanize(a.appointmentType),
    kind: "appointment",
    tone: STATUS_TONE[a.status],
  };
}

function taskToEvent(t: Task): CalendarEvent | null {
  if (!t.dueAt) return null;
  if (t.status === "done" || t.status === "cancelled") return null;
  const due = new Date(t.dueAt);
  const today = new Date();
  const tone: CalendarEventTone =
    due.getTime() < Date.now()
      ? "danger"
      : due.toDateString() === today.toDateString()
        ? "warning"
        : "default";
  return {
    id: t.id,
    start: due,
    title: t.title,
    subtitle: `Task · ${humanize(t.taskType)}`,
    kind: "task",
    tone,
  };
}

type StatusFilter = "all" | "upcoming" | "today" | "cancelled" | "completed";

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "upcoming", label: "Upcoming" },
  { key: "today", label: "Today" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

// Single source of truth for the appointment-duration bounds — the Zod
// schema and the number input's min/max attributes share these.
const DURATION_MIN_MINUTES = 5;
const DURATION_MAX_MINUTES = 480;

const AppointmentFormSchema = z.object({
  contactId: z.string().uuid("Pick a contact."),
  rooftopId: z.string().uuid("Pick a rooftop."),
  appointmentType: AppointmentTypeSchema,
  status: AppointmentStatusSchema,
  scheduledStartAt: z.string().min(1, "Pick a date and time."),
  durationMinutes: z
    .number({ invalid_type_error: "Duration must be a number." })
    .int()
    .min(DURATION_MIN_MINUTES)
    .max(DURATION_MAX_MINUTES),
  assignedUserId: z.string().uuid().or(z.literal("none")),
  transportType: AppointmentTransportTypeSchema.or(z.literal("none")),
  vehicleVin: z.union([z.literal(""), VinSchema]).optional(),
  notes: z.string().trim().max(4000).optional(),
});

type AppointmentFormValues = z.infer<typeof AppointmentFormSchema>;

// `scheduledStartAt` is computed when the dialog opens (not here at
// module load) so a long-lived tab doesn't prefill a time in the past.
const EMPTY: AppointmentFormValues = {
  contactId: "",
  rooftopId: "",
  appointmentType: "sales",
  status: "unconfirmed",
  scheduledStartAt: "",
  durationMinutes: 30,
  assignedUserId: "none",
  transportType: "none",
  vehicleVin: "",
  notes: "",
};

function defaultSlot(zone: string): string {
  // Next full hour ON THE STORE'S CLOCK, not the admin's.
  const date = wallClockDate(new Date(), zone);
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return toLocalInput(date);
}

function toLocalInput(value: Date): string {
  const yyyy = value.getFullYear();
  const mm = String(value.getMonth() + 1).padStart(2, "0");
  const dd = String(value.getDate()).padStart(2, "0");
  const hh = String(value.getHours()).padStart(2, "0");
  const mi = String(value.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function toEditValues(appointment: Appointment, zone: string): AppointmentFormValues {
  return {
    contactId: appointment.contactId,
    rooftopId: appointment.rooftopId,
    appointmentType: appointment.appointmentType,
    status: appointment.status,
    // The datetime-local field shows/edits the STORE's wall time.
    scheduledStartAt: utcToWallTime(appointment.scheduledStartAt, zone),
    durationMinutes: appointment.durationMinutes,
    assignedUserId: appointment.assignedUserId ?? "none",
    transportType: appointment.transportType ?? "none",
    vehicleVin: appointment.vehicleVin ?? "",
    notes: appointment.notes ?? "",
  };
}

function toUpdatePayload(values: AppointmentFormValues, zone: string): UpdateAppointmentDto {
  return {
    appointmentType: values.appointmentType,
    status: values.status,
    // The form value is store wall time — convert via the rooftop
    // zone, NOT the browser's (the old `new Date(naive)` parse shifted
    // bookings by the admin-vs-store timezone delta).
    scheduledStartAt: wallTimeToUtc(values.scheduledStartAt, zone).toISOString(),
    durationMinutes: values.durationMinutes,
    assignedUserId: values.assignedUserId === "none" ? null : values.assignedUserId,
    transportType: values.transportType === "none" ? null : values.transportType,
    vehicleVin: values.vehicleVin?.trim() ? values.vehicleVin.trim().toUpperCase() : null,
    notes: values.notes?.trim() ? values.notes.trim() : null,
  };
}

export function DealershipCalendarPage() {
  const { dealershipId } = useParams();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("filter") as StatusFilter | null) ?? "upcoming";
  const [editing, setEditing] = useState<Appointment | "new" | null>(null);
  const [taskEditing, setTaskEditing] = useState<Task | "new" | null>(null);
  const [presetStart, setPresetStart] = useState<string | null>(null);
  const [focusDate, setFocusDate] = useState(() => new Date());
  const [showAppointments, setShowAppointments] = useState(true);
  const [showTasks, setShowTasks] = useState(true);

  const paramView = params.get("view");
  const view: ViewMode = isViewMode(paramView) ? paramView : "calendar";
  const calView: CalendarView =
    params.get("cal") === "day" || params.get("cal") === "month"
      ? (params.get("cal") as CalendarView)
      : "week";

  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });
  // Fetch only the visible date window (a 3-month span aligned to the
  // focused month) instead of the earliest page of every appointment ever.
  const apptWindow = useMemo(() => {
    const y = focusDate.getFullYear();
    const m = focusDate.getMonth();
    return {
      from: new Date(y, m - 1, 1).toISOString(),
      to: new Date(y, m + 2, 1).toISOString(),
    };
  }, [focusDate]);
  const appointmentsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "appointments", apptWindow.from, apptWindow.to],
    queryFn: () => adminApi.listAppointments(dealershipId!, apptWindow),
    enabled: Boolean(dealershipId),
  });
  // The calendar grid plots tasks by due date — fetch only the visible
  // window (same 3-month span as appointments) so a busy board doesn't
  // silently drop tasks past the first page.
  const tasksQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "tasks", "calendar", apptWindow.from, apptWindow.to],
    queryFn: () =>
      adminApi.listTasks(dealershipId!, {
        from: apptWindow.from,
        to: apptWindow.to,
        pageSize: 100,
      }),
    enabled: Boolean(dealershipId),
  });
  // The contact list doubles as the booking-dialog picker and the
  // id→name map for every row — fetch the largest page the API allows
  // (the backend defaults to 25 and clamps to 100).
  const contactsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contacts"],
    queryFn: () => adminApi.listContacts(dealershipId!, "", 1, 100),
    enabled: Boolean(dealershipId),
  });

  const rooftops = detailQuery.data?.rooftops ?? [];
  const users = detailQuery.data?.users ?? [];
  const contacts = useMemo(() => contactsQuery.data?.items ?? [], [contactsQuery.data]);
  const all = appointmentsQuery.data?.items ?? [];
  const allTasks = tasksQuery.data?.items ?? [];

  const contactName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of contacts) {
      const composed = c.businessName ?? [c.firstName, c.lastName].filter(Boolean).join(" ");
      map.set(c.id, composed || c.primaryEmail || c.primaryPhone || c.fallbackLabel || "Anonymous");
    }
    return map;
  }, [contacts]);

  const rooftopName = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rooftops) map.set(r.id, r.name);
    return map;
  }, [rooftops]);

  const userName = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.id, u.displayName ?? u.email ?? "Unnamed");
    return map;
  }, [users]);

  // rooftopId → IANA zone; unknown/missing rooftop falls back to the
  // dealership's primary timezone, then the admin's browser zone.
  const primaryTimezone = detailQuery.data?.primaryTimezone ?? null;
  const zoneFor = useMemo<ZoneFor>(() => {
    const map = new Map<string, string>();
    for (const r of rooftops) map.set(r.id, r.timezone);
    const fallback = primaryTimezone ?? rooftops[0]?.timezone ?? BROWSER_ZONE;
    return (rooftopId) => (rooftopId ? (map.get(rooftopId) ?? fallback) : fallback);
  }, [rooftops, primaryTimezone]);

  const filtered = useMemo(
    () => filterAppointments(all, filter, zoneFor),
    [all, filter, zoneFor],
  );

  const calendarEvents = useMemo(() => {
    const events: CalendarEvent[] = [];
    if (showAppointments) {
      for (const a of all) events.push(appointmentToEvent(a, contactName, zoneFor));
    }
    if (showTasks) {
      for (const t of allTasks) {
        const ev = taskToEvent(t);
        if (ev) events.push(ev);
      }
    }
    return events;
  }, [all, allTasks, contactName, showAppointments, showTasks, zoneFor]);

  const listTasks = useMemo(
    () =>
      [...allTasks].sort((a, b) => {
        const ad = a.dueAt ?? a.todoAt ?? a.createdAt;
        const bd = b.dueAt ?? b.todoAt ?? b.createdAt;
        return new Date(ad).getTime() - new Date(bd).getTime();
      }),
    [allTasks],
  );

  function setFilter(next: StatusFilter) {
    const updated = new URLSearchParams(params);
    updated.set("filter", next);
    setParams(updated, { replace: true });
  }

  function setParam(key: string, value: string) {
    const updated = new URLSearchParams(params);
    updated.set(key, value);
    setParams(updated, { replace: true });
  }

  function openCreate(start: string | null) {
    setPresetStart(start);
    setEditing("new");
  }

  function openEventEdit(event: CalendarEvent) {
    if (event.kind === "task") {
      const task = allTasks.find((t) => t.id === event.id);
      if (task) setTaskEditing(task);
      return;
    }
    const match = all.find((a) => a.id === event.id);
    if (match) setEditing(match);
  }

  const canBook = rooftops.length > 0 && contacts.length > 0;
  const canCreateTask = contacts.length > 0;
  // Explain a disabled action instead of leaving a dead button — a
  // failed contacts/detail fetch otherwise looks like "booking is off".
  const actionsDisabledReason =
    detailQuery.isError || contactsQuery.isError
      ? "Couldn't load dealership data — refresh and try again."
      : detailQuery.isLoading || contactsQuery.isLoading
        ? "Loading dealership data…"
        : "Add a rooftop and a contact first.";
  const dealershipTz = detailQuery.data?.primaryTimezone ?? null;

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <PageHeader
        eyebrow="Schedule"
        title="Calendar"
        description="Appointments and tasks — on a calendar, list, or board."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center rounded-md bg-muted p-0.5">
              {(
                [
                  { key: "calendar", label: "Calendar", icon: CalendarDays },
                  { key: "list", label: "List", icon: List },
                  { key: "board", label: "Board", icon: Columns3 },
                ] as const
              ).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setParam("view", key)}
                  aria-label={label}
                  aria-pressed={view === key}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-sm px-3 py-1 text-sm font-medium transition-colors",
                    view === key
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => setTaskEditing("new")}
              disabled={!canCreateTask}
              title={canCreateTask ? undefined : actionsDisabledReason}
            >
              <ListPlus className="h-4 w-4" />
              New task
            </Button>
            <Button
              onClick={() => openCreate(null)}
              disabled={!canBook}
              title={canBook ? undefined : actionsDisabledReason}
            >
              <Plus className="h-4 w-4" />
              Book appointment
            </Button>
          </div>
        }
      />

      <CalendarLegend
        showAppointments={showAppointments}
        showTasks={showTasks}
        onToggleAppointments={() => setShowAppointments((v) => !v)}
        onToggleTasks={() => setShowTasks((v) => !v)}
      />

      <p className="text-xs text-muted-foreground">
        Appointment times are shown in each store&apos;s local timezone
        {dealershipTz ? ` (default ${dealershipTz})` : ""}; task due times are in
        your local timezone.
      </p>

      {view === "list" && showAppointments && (
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-sm transition-colors",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted",
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      )}

      {view === "calendar" ? (
        appointmentsQuery.isError || tasksQuery.isError ? (
          <PageError
            title="Couldn't load the schedule"
            description="Try refreshing the page."
          />
        ) : (
          <div className="h-[50rem]">
            <Calendar
              events={calendarEvents}
              view={calView}
              focusDate={focusDate}
              onViewChange={(v) => setParam("cal", v)}
              onFocusDateChange={setFocusDate}
              onEventClick={openEventEdit}
              onSlotClick={
                // Omitting the handler disables the grid's slot buttons.
                //
                // The grid operates in STORE wall time (events are laid
                // out via wallClockDate), so the slot Date's COMPONENTS
                // are the store wall time clicked — extract them
                // (toLocalInput) and let the form's wallTimeToUtc pin
                // the zone. Do NOT convert the slot as an instant.
                canBook ? (slot) => openCreate(toLocalInput(slot)) : undefined
              }
              isLoading={appointmentsQuery.isLoading || tasksQuery.isLoading}
            />
          </div>
        )
      ) : view === "board" ? (
        <CalendarBoard
          appointments={all}
          tasks={allTasks}
          showAppointments={showAppointments}
          showTasks={showTasks}
          contactName={contactName}
          userName={userName}
          zoneFor={zoneFor}
          isLoading={appointmentsQuery.isLoading || tasksQuery.isLoading}
          isError={appointmentsQuery.isError || tasksQuery.isError}
          onEdit={(a) => setEditing(a)}
          onTaskEdit={(t) => setTaskEditing(t)}
          onWindowChange={setFocusDate}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {showAppointments && (
            <Section title={`${filtered.length} of ${all.length} appointments`}>
              {appointmentsQuery.isLoading ? (
                <PageLoading message="Loading appointments…" />
              ) : appointmentsQuery.isError ? (
                <PageError
                  title="Couldn't load appointments"
                  description="Try refreshing the page."
                />
              ) : filtered.length === 0 ? (
                <EmptyState
                  icon={<CalendarClock className="h-5 w-5" />}
                  title={all.length === 0 ? "No appointments yet" : "Nothing matches this filter"}
                  description={
                    all.length === 0
                      ? "Book the first one — pick a contact, rooftop, type, and slot."
                      : "Try a different filter or clear it."
                  }
                  action={
                    all.length === 0 && canBook ? (
                      <Button onClick={() => openCreate(null)}>
                        <Plus className="h-4 w-4" />
                        Book appointment
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <>
                  <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contact</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>When</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Rooftop</TableHead>
                          <TableHead>Assigned</TableHead>
                          <TableHead>Vehicle</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-12 text-right">Edit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((appointment) => (
                          <TableRow key={appointment.id}>
                            <TableCell className="font-medium">
                              {contactName.get(appointment.contactId) ?? "—"}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {humanize(appointment.appointmentType)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {formatDateTimeInZone(
                                appointment.scheduledStartAt,
                                zoneFor(appointment.rooftopId),
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {appointment.durationMinutes} min
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {rooftopName.get(appointment.rooftopId) ?? "—"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {appointment.assignedUserId
                                ? (userName.get(appointment.assignedUserId) ?? "—")
                                : "—"}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {appointment.vehicleVin ?? "—"}
                            </TableCell>
                            <TableCell>
                              <Badge variant={STATUS_VARIANT[appointment.status]}>
                                {humanize(appointment.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Edit appointment"
                                onClick={() => setEditing(appointment)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableBleed>
                  <MobileList>
                    {filtered.map((appointment) => (
                      <MobileListRow
                        key={appointment.id}
                        primary={contactName.get(appointment.contactId) ?? "—"}
                        secondary={`${formatDateTimeInZone(appointment.scheduledStartAt, zoneFor(appointment.rooftopId))} · ${appointment.durationMinutes} min`}
                        tertiary={
                          <>
                            {rooftopName.get(appointment.rooftopId) ?? "—"}
                            {" · "}
                            {appointment.assignedUserId
                              ? (userName.get(appointment.assignedUserId) ?? "—")
                              : "Unassigned"}
                            {appointment.vehicleVin ? (
                              <span className="font-mono"> · {appointment.vehicleVin}</span>
                            ) : null}
                          </>
                        }
                        meta={
                          <>
                            <Badge variant="secondary" className="text-[10px] uppercase">
                              {humanize(appointment.appointmentType)}
                            </Badge>
                            <Badge
                              variant={STATUS_VARIANT[appointment.status]}
                              className="text-[10px] uppercase"
                            >
                              {humanize(appointment.status)}
                            </Badge>
                          </>
                        }
                        trailing={
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Edit appointment"
                            onClick={() => setEditing(appointment)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        }
                      />
                    ))}
                  </MobileList>
                </>
              )}
            </Section>
          )}
          {showTasks && (
            <Section title={`${listTasks.length} tasks`}>
              {tasksQuery.isLoading ? (
                <PageLoading message="Loading tasks…" />
              ) : tasksQuery.isError ? (
                <PageError
                  title="Couldn't load tasks"
                  description="Try refreshing the page."
                />
              ) : listTasks.length === 0 ? (
                <EmptyState
                  icon={<CalendarClock className="h-5 w-5" />}
                  title="No tasks"
                  description="Tasks for the dealership team show up here."
                />
              ) : (
                <ul className="grid gap-2">
                  {listTasks.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => setTaskEditing(t)}
                        className="flex w-full items-center justify-between gap-3 rounded-md border bg-card px-4 py-2.5 text-left text-sm transition-colors hover:bg-muted/40"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate font-medium">{t.title}</span>
                          <Badge
                            variant="outline"
                            className="shrink-0 text-[10px] uppercase"
                          >
                            {humanize(t.taskType)}
                          </Badge>
                        </span>
                        <span className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                          {t.contactId && contactName.get(t.contactId) && (
                            <span className="hidden md:inline">{contactName.get(t.contactId)}</span>
                          )}
                          {t.dueAt ? <span>Due {formatDateTime(t.dueAt)}</span> : null}
                          <Badge
                            variant="secondary"
                            className="text-[10px] uppercase"
                          >
                            {humanize(t.status)}
                          </Badge>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}
          {!showAppointments && !showTasks && (
            <Section title="Nothing to show">
              <EmptyState
                icon={<CalendarClock className="h-5 w-5" />}
                title="No layer selected"
                description="Turn on Appointments or Tasks above to see them here."
              />
            </Section>
          )}
        </div>
      )}

      {editing !== null && dealershipId && (
        <AppointmentFormDialog
          mode={editing === "new" ? "create" : "edit"}
          appointment={editing === "new" ? null : editing}
          presetStart={presetStart}
          contacts={contacts}
          rooftops={rooftops}
          users={users}
          dealershipId={dealershipId}
          onClose={() => {
            setEditing(null);
            setPresetStart(null);
          }}
        />
      )}

      {taskEditing === "new" && dealershipId && (
        <NewTaskDialog
          dealershipId={dealershipId}
          contacts={contacts}
          rooftops={rooftops}
          users={users}
          onClose={() => setTaskEditing(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "tasks"] })}
        />
      )}
      {taskEditing !== null && taskEditing !== "new" && dealershipId && (
        <TaskEditDialog
          dealershipId={dealershipId}
          task={taskEditing}
          users={users}
          onClose={() => setTaskEditing(null)}
        />
      )}
    </div>
  );
}

function filterAppointments(
  items: Appointment[],
  filter: StatusFilter,
  zoneFor: ZoneFor,
): Appointment[] {
  if (filter === "all") return items;
  if (filter === "completed") return items.filter((a) => a.status === "completed");
  if (filter === "cancelled")
    return items.filter((a) => a.status === "cancelled" || a.status === "no_show");
  const now = Date.now();
  if (filter === "today") {
    // "Today" on the STORE's calendar, not the admin's.
    return items.filter((a) => {
      const zone = zoneFor(a.rooftopId);
      return (
        utcToWallTime(a.scheduledStartAt, zone).slice(0, 10) ===
        utcToWallTime(new Date(), zone).slice(0, 10)
      );
    });
  }
  return items.filter((a) => {
    if (a.status === "completed" || a.status === "cancelled" || a.status === "no_show") return false;
    return new Date(a.scheduledStartAt).getTime() >= now;
  });
}

function CalendarLegend({
  showAppointments,
  showTasks,
  onToggleAppointments,
  onToggleTasks,
}: {
  showAppointments: boolean;
  showTasks: boolean;
  onToggleAppointments: () => void;
  onToggleTasks: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border bg-card px-3 py-2 text-xs">
      <button
        type="button"
        onClick={onToggleAppointments}
        aria-pressed={showAppointments}
        className={cn(
          "flex items-center gap-1.5 rounded px-1.5 py-0.5 transition",
          showAppointments ? "text-foreground" : "text-muted-foreground opacity-50",
        )}
      >
        <span className="h-3 w-3 rounded-sm bg-primary" aria-hidden />
        Appointments
      </button>
      <button
        type="button"
        onClick={onToggleTasks}
        aria-pressed={showTasks}
        className={cn(
          "flex items-center gap-1.5 rounded px-1.5 py-0.5 transition",
          showTasks ? "text-foreground" : "text-muted-foreground opacity-50",
        )}
      >
        <span
          className="h-3 w-3 rounded-sm border border-dashed border-foreground/50 bg-background"
          aria-hidden
        />
        Tasks
      </button>
      <span className="ml-auto flex items-center gap-3 text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-destructive" aria-hidden />
          Overdue
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-warning" aria-hidden />
          Due today
        </span>
      </span>
    </div>
  );
}

export function AppointmentFormDialog({
  mode,
  appointment,
  presetStart,
  contacts,
  rooftops,
  users,
  dealershipId,
  onClose,
}: {
  mode: "create" | "edit";
  appointment: Appointment | null;
  presetStart: string | null;
  contacts: { id: string; firstName: string | null; lastName: string | null; businessName: string | null; primaryEmail: string | null; primaryPhone: string | null }[];
  /** Rooftop timezones drive the form's wall-time ↔ UTC conversion. */
  rooftops: { id: string; name: string; timezone: string }[];
  users: { id: string; displayName: string | null; email: string | null; kind: string }[];
  dealershipId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // The form's date field reads/writes the STORE's wall clock — the
  // selected rooftop's zone anchors both directions of the conversion.
  const zoneOf = (rooftopId: string | null | undefined): string => {
    const zone = rooftopId
      ? rooftops.find((r) => r.id === rooftopId)?.timezone
      : undefined;
    return (
      zone ?? rooftops[0]?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  };
  // When booked from a contact row the caller passes a one-item
  // `contacts` array — pre-select that contact so the operator only
  // has to pick a time. The main Calendar page passes the full
  // contact list and the dropdown stays empty. Rooftop defaults to
  // the first one; contacts are dealership-scoped so they no longer
  // carry a rooftop to inherit.
  // Snapshot once at mount — defaultSlot() tracks the wall clock, so
  // recomputing it per render would read as a dirty form after an hour
  // boundary. useZodForm seeds from the same first-render value.
  const [initial] = useState(() =>
    appointment
      ? toEditValues(appointment, zoneOf(appointment.rooftopId))
      : {
          ...EMPTY,
          contactId: contacts.length === 1 ? (contacts[0]?.id ?? "") : "",
          rooftopId: rooftops[0]?.id ?? "",
          scheduledStartAt: presetStart ?? defaultSlot(zoneOf(rooftops[0]?.id)),
        },
  );
  const form = useZodForm(AppointmentFormSchema, initial);

  const createMutation = useMutation({
    mutationFn: (input: AppointmentFormValues) =>
      adminApi.createAppointment(dealershipId, {
        contactId: input.contactId,
        rooftopId: input.rooftopId,
        appointmentType: input.appointmentType,
        status: input.status,
        // The typed value is wall time at the SELECTED rooftop.
        scheduledStartAt: wallTimeToUtc(
          input.scheduledStartAt,
          zoneOf(input.rooftopId),
        ).toISOString(),
        durationMinutes: input.durationMinutes,
        assignedUserId: input.assignedUserId === "none" ? null : input.assignedUserId,
        transportType: input.transportType === "none" ? null : input.transportType,
        vehicleVin: input.vehicleVin?.trim() ? input.vehicleVin.trim().toUpperCase() : null,
        notes: input.notes?.trim() ? input.notes.trim() : null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "appointments"] });
      toast.success("Appointment booked");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: AppointmentFormValues) =>
      adminApi.updateAppointment(
        dealershipId,
        appointment!.id,
        toUpdatePayload(input, zoneOf(input.rooftopId)),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "appointments"] });
      toast.success("Appointment updated");
      // Close like create does — leaving the dialog open keeps stale
      // form state around (e.g. re-saving after a cancel would revert it).
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") createMutation.mutate(result.data);
    else updateMutation.mutate(result.data);
  }

  function cancelAppointment() {
    if (!appointment) return;
    if (!window.confirm("Cancel this appointment?")) return;
    updateMutation.mutate({ ...form.values, status: "cancelled" });
  }

  const { values, errors, setField } = form;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isDirty = JSON.stringify(values) !== JSON.stringify(initial);
  const humanUsers = users.filter((u) => u.kind === "human");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Book appointment" : "Edit appointment"}</DialogTitle>
          <DialogDescription>
            Schedule a visit. Attach a contact and rooftop; optionally an assignee and vehicle.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-5 overflow-y-auto -mx-1 px-1">
          <FieldGroup title="Who & where">
            <FormField label="Contact" required error={errors.contactId}>
              <Select
                value={values.contactId}
                onValueChange={(v) => setField("contactId", v)}
                disabled={mode === "edit"}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => {
                    const composed =
                      contact.businessName ??
                      [contact.firstName, contact.lastName].filter(Boolean).join(" ");
                    return (
                      <SelectItem key={contact.id} value={contact.id}>
                        {composed || contact.primaryEmail || contact.primaryPhone || "Anonymous"}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Rooftop" required error={errors.rooftopId}>
              <Select
                value={values.rooftopId}
                onValueChange={(v) => setField("rooftopId", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a rooftop" />
                </SelectTrigger>
                <SelectContent>
                  {rooftops.map((rooftop) => (
                    <SelectItem key={rooftop.id} value={rooftop.id}>
                      {rooftop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FieldGroup>

          <FieldGroup title="Schedule">
            <FormField label="Type" required error={errors.appointmentType}>
              <Select
                value={values.appointmentType}
                onValueChange={(v) => setField("appointmentType", v as AppointmentType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AppointmentTypeSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {humanize(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Status" required error={errors.status}>
              <Select
                value={values.status}
                onValueChange={(v) => setField("status", v as AppointmentStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AppointmentStatusSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {humanize(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label="Date & time"
              required
              error={errors.scheduledStartAt}
              description={`Store time — ${zoneOf(values.rooftopId)}`}
            >
              <Input
                type="datetime-local"
                value={values.scheduledStartAt}
                onChange={(e) => setField("scheduledStartAt", e.target.value)}
              />
            </FormField>
            <FormField label="Duration (min)" required error={errors.durationMinutes}>
              <Input
                type="number"
                min={DURATION_MIN_MINUTES}
                max={DURATION_MAX_MINUTES}
                step={5}
                value={values.durationMinutes}
                onChange={(e) => setField("durationMinutes", Number(e.target.value) || 0)}
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Assignment & logistics">
            <FormField label="Assigned to" error={errors.assignedUserId}>
              <Select
                value={values.assignedUserId}
                onValueChange={(v) => setField("assignedUserId", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {humanUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.displayName ?? user.email ?? "Unnamed"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Transport" error={errors.transportType}>
              <Select
                value={values.transportType}
                onValueChange={(v) => setField("transportType", v as TransportType | "none")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No transport</SelectItem>
                  {AppointmentTransportTypeSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {humanize(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField className="sm:col-span-2" label="Vehicle VIN" optional error={errors.vehicleVin}>
              <Input
                value={values.vehicleVin ?? ""}
                onChange={(e) => setField("vehicleVin", e.target.value)}
                placeholder="If a specific vehicle is involved"
                className="font-mono"
              />
            </FormField>
            <FormField className="sm:col-span-2" label="Notes" optional error={errors.notes}>
              <Textarea
                value={values.notes ?? ""}
                onChange={(e) => setField("notes", e.target.value)}
                rows={3}
                placeholder="Anything operators or the customer should know."
              />
            </FormField>
          </FieldGroup>
        </div>

        <DialogFooter>
          {mode === "edit" && values.status !== "cancelled" && (
            <Button variant="destructive" onClick={cancelAppointment} disabled={isPending}>
              Cancel appointment
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? "Saving…"
              : mode === "create"
                ? "Book appointment"
                : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

const TaskFormSchema = z.object({
  title: z.string().trim().min(1, "Title is required.").max(200),
  description: z.string().trim().max(8000).optional().or(z.literal("")),
  taskType: TaskTypeSchema,
  reason: TaskReasonSchema.or(z.literal("none")),
  rooftopId: z.string().uuid("Pick a rooftop."),
  contactId: z.string().uuid("Pick a contact."),
  assignedUserId: z.string().optional().or(z.literal("")),
  dueAt: z.string().optional().or(z.literal("")),
});
type TaskFormValues = z.infer<typeof TaskFormSchema>;

export function NewTaskDialog({
  dealershipId,
  contacts,
  rooftops,
  users,
  onClose,
  onSaved,
  presetContactId,
}: {
  dealershipId: string;
  contacts: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    businessName: string | null;
    primaryEmail: string | null;
    primaryPhone: string | null;
  }[];
  rooftops: { id: string; name: string }[];
  users: { id: string; displayName: string | null; email: string | null; kind: string }[];
  onClose: () => void;
  onSaved?: () => void;
  /** Pre-fill the contact for the contact-page quick-add. */
  presetContactId?: string;
}) {
  const qc = useQueryClient();
  const humans = users.filter((u) => u.kind === "human");

  const initial: TaskFormValues = {
    title: "",
    description: "",
    taskType: "other",
    reason: "none",
    rooftopId: rooftops[0]?.id ?? "",
    contactId: presetContactId ?? "",
    assignedUserId: "",
    dueAt: "",
  };
  const form = useZodForm(TaskFormSchema, initial);
  const { values, errors, setField } = form;
  const isDirty = JSON.stringify(values) !== JSON.stringify(initial);

  const create = useMutation({
    mutationFn: (input: TaskFormValues) => {
      return adminApi.createTask(dealershipId, {
        rooftopId: input.rooftopId,
        contactId: input.contactId,
        title: input.title,
        description: input.description?.trim() ? input.description.trim() : null,
        taskType: input.taskType,
        reason: input.reason === "none" ? null : input.reason,
        assignedUserId: input.assignedUserId ? input.assignedUserId : null,
        dueAt: input.dueAt ? new Date(input.dueAt).toISOString() : null,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "tasks"] });
      onSaved?.();
      toast.success("Task created");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    create.mutate(result.data);
  }

  function contactLabel(c: {
    firstName: string | null;
    lastName: string | null;
    businessName: string | null;
    primaryPhone: string | null;
    primaryEmail: string | null;
  }): string {
    const name =
      c.businessName ?? [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
    return name || c.primaryEmail || c.primaryPhone || "(unnamed contact)";
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
          <DialogDescription>
            A task is always tied to a contact. Assign it now or leave it for the queue.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-4 overflow-y-auto -mx-1 px-1">
          <FormField label="Title" required error={errors.title}>
            <Input
              value={values.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="e.g. Call back about the trade-in"
              autoFocus
            />
          </FormField>

          <FormField label="Contact" required error={errors.contactId}>
            <Select
              value={values.contactId}
              onValueChange={(v) => setField("contactId", v)}
              disabled={Boolean(presetContactId)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick a contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {contactLabel(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Rooftop" required error={errors.rooftopId}>
            <Select
              value={values.rooftopId}
              onValueChange={(v) => setField("rooftopId", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick a rooftop" />
              </SelectTrigger>
              <SelectContent>
                {rooftops.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Type" error={errors.taskType}>
              <Select
                value={values.taskType}
                onValueChange={(v) => setField("taskType", v as TaskType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TaskTypeSchema.options
                    .filter((t) => t !== "conversation.respond")
                    .map((t) => (
                      <SelectItem key={t} value={t}>
                        {humanize(t)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Reason" error={errors.reason}>
              <Select
                value={values.reason}
                onValueChange={(v) => setField("reason", v as TaskReason | "none")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No reason</SelectItem>
                  {TaskReasonSchema.options.map((r) => (
                    <SelectItem key={r} value={r}>
                      {humanize(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Assignee">
              <Select
                value={values.assignedUserId || "__unassigned"}
                onValueChange={(v) =>
                  setField("assignedUserId", v === "__unassigned" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned">Unassigned</SelectItem>
                  {humans.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.displayName ?? u.email ?? "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Due" optional>
              <Input
                type="datetime-local"
                value={values.dueAt ?? ""}
                onChange={(e) => setField("dueAt", e.target.value)}
              />
            </FormField>
          </div>

          <FormField label="Description" optional error={errors.description}>
            <Textarea
              value={values.description ?? ""}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
            />
          </FormField>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EditTaskFormSchema = z.object({
  title: z.string().trim().min(1, "Title is required.").max(200),
  description: z.string().trim().max(8000).optional().or(z.literal("")),
  taskType: TaskTypeSchema,
  reason: TaskReasonSchema.or(z.literal("none")),
  dueAt: z.string().optional().or(z.literal("")),
  assignedUserId: z.string().optional().or(z.literal("")),
});
type EditTaskFormValues = z.infer<typeof EditTaskFormSchema>;

function toDateTimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export function TaskEditDialog({
  dealershipId,
  task,
  users,
  onClose,
}: {
  dealershipId: string;
  task: Task;
  users: { id: string; displayName: string | null; email: string | null; kind: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const humans = users.filter((u) => u.kind === "human");

  const initial: EditTaskFormValues = {
    title: task.title,
    description: task.description ?? "",
    taskType: task.taskType,
    reason: task.reason ?? "none",
    dueAt: toDateTimeLocal(task.dueAt),
    assignedUserId: task.assignedUserId ?? "",
  };
  const form = useZodForm(EditTaskFormSchema, initial);
  const { values, errors, setField } = form;
  const isDirty = JSON.stringify(values) !== JSON.stringify(initial);

  async function afterMutation(message: string): Promise<void> {
    await qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "tasks"] });
    toast.success(message);
    onClose();
  }

  const save = useMutation({
    mutationFn: async (input: EditTaskFormValues) => {
      await adminApi.updateTask(dealershipId, task.id, {
        title: input.title,
        description: input.description?.trim() ? input.description.trim() : null,
        taskType: input.taskType,
        reason: input.reason === "none" ? null : input.reason,
        dueAt: input.dueAt ? new Date(input.dueAt).toISOString() : null,
      });
      const nextAssignee = input.assignedUserId || null;
      if (nextAssignee !== (task.assignedUserId ?? null)) {
        await adminApi.assignTask(dealershipId, task.id, { assignedUserId: nextAssignee });
      }
    },
    onSuccess: () => afterMutation("Task saved"),
    onError: (e: Error) => toast.error(e.message),
  });
  const start = useMutation({
    mutationFn: () => adminApi.startTask(dealershipId, task.id),
    onSuccess: () => afterMutation("Task marked in progress"),
    onError: (e: Error) => toast.error(e.message),
  });
  const complete = useMutation({
    mutationFn: () => adminApi.completeTask(dealershipId, task.id),
    onSuccess: () => afterMutation("Task completed"),
    onError: (e: Error) => toast.error(e.message),
  });
  const cancel = useMutation({
    mutationFn: () => adminApi.cancelTask(dealershipId, task.id),
    onSuccess: () => afterMutation("Task cancelled"),
    onError: (e: Error) => toast.error(e.message),
  });

  const busy =
    save.isPending || start.isPending || complete.isPending || cancel.isPending;
  const closed = task.status === "done" || task.status === "cancelled";
  const canStart = task.status === "open" || task.status === "snoozed";

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    save.mutate(result.data);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>Edit task</DialogTitle>
          <DialogDescription>
            {humanize(task.taskType)} · {humanize(task.status)}
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-4 overflow-y-auto -mx-1 px-1">
          <FormField label="Title" required error={errors.title}>
            <Input
              value={values.title}
              onChange={(e) => setField("title", e.target.value)}
              autoFocus
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Type" error={errors.taskType}>
              <Select
                value={values.taskType}
                onValueChange={(v) => setField("taskType", v as TaskType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TaskTypeSchema.options.map((t) => (
                    <SelectItem key={t} value={t}>
                      {humanize(t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Reason" error={errors.reason}>
              <Select
                value={values.reason}
                onValueChange={(v) => setField("reason", v as TaskReason | "none")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No reason</SelectItem>
                  {TaskReasonSchema.options.map((r) => (
                    <SelectItem key={r} value={r}>
                      {humanize(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Assignee">
              <Select
                value={values.assignedUserId || "__unassigned"}
                onValueChange={(v) =>
                  setField("assignedUserId", v === "__unassigned" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned">Unassigned</SelectItem>
                  {humans.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.displayName ?? u.email ?? "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Due" optional>
              <Input
                type="datetime-local"
                value={values.dueAt ?? ""}
                onChange={(e) => setField("dueAt", e.target.value)}
              />
            </FormField>
          </div>

          <FormField label="Description" optional error={errors.description}>
            <Textarea
              value={values.description ?? ""}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
            />
          </FormField>

          {closed ? (
            <p className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              This task is {humanize(task.status).toLowerCase()}. Field edits are still saved,
              but its lifecycle is closed.
            </p>
          ) : (
            <div className="grid gap-2 rounded-md border bg-muted/30 p-3">
              <span className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Actions
              </span>
              <div className="flex flex-wrap gap-2">
                {canStart && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => start.mutate()}
                    disabled={busy}
                  >
                    Mark in progress
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => cancel.mutate()}
                  disabled={busy}
                >
                  Cancel task
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => complete.mutate()}
                  disabled={busy}
                >
                  Complete task
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button onClick={submit} disabled={busy}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Board view
// ---------------------------------------------------------------------------

const BOARD_DAYS = 7;

function startOfLocalDay(value: Date): Date {
  const next = new Date(value);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(value: Date, days: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function CalendarBoard({
  appointments,
  tasks,
  showAppointments,
  showTasks,
  contactName,
  userName,
  zoneFor,
  isLoading,
  isError,
  onEdit,
  onTaskEdit,
  onWindowChange,
}: {
  appointments: Appointment[];
  tasks: Task[];
  showAppointments: boolean;
  showTasks: boolean;
  contactName: Map<string, string>;
  userName: Map<string, string>;
  zoneFor: ZoneFor;
  isLoading: boolean;
  isError: boolean;
  onEdit: (appointment: Appointment) => void;
  onTaskEdit: (task: Task) => void;
  /** Keeps the page-level fetch window following the board's paging. */
  onWindowChange: (start: Date) => void;
}) {
  const today = startOfLocalDay(new Date());
  const [windowStart, setWindowStart] = useState<Date>(today);

  // Data is fetched for a 3-month span keyed to the page's focusDate —
  // move it along with the board so paging past the span doesn't render
  // confidently empty days.
  function moveWindow(next: Date) {
    setWindowStart(next);
    onWindowChange(next);
  }

  const days = useMemo(
    () => Array.from({ length: BOARD_DAYS }, (_, i) => addDays(windowStart, i)),
    [windowStart],
  );
  const apptsByDay = useMemo(() => {
    const buckets: Appointment[][] = days.map(() => []);
    if (showAppointments) {
      for (const appointment of appointments) {
        // Bucket by the STORE's calendar day, not the admin's.
        const start = wallClockDate(
          appointment.scheduledStartAt,
          zoneFor(appointment.rooftopId),
        );
        const idx = days.findIndex((day) => isSameLocalDay(day, start));
        if (idx >= 0) buckets[idx]!.push(appointment);
      }
      for (const bucket of buckets) {
        bucket.sort(
          (a, b) =>
            new Date(a.scheduledStartAt).getTime() -
            new Date(b.scheduledStartAt).getTime(),
        );
      }
    }
    return buckets;
  }, [appointments, days, showAppointments, zoneFor]);
  const tasksByDay = useMemo(() => {
    const buckets: Task[][] = days.map(() => []);
    if (showTasks) {
      for (const task of tasks) {
        if (!task.dueAt) continue;
        const idx = days.findIndex((day) =>
          isSameLocalDay(day, new Date(task.dueAt!)),
        );
        if (idx >= 0) buckets[idx]!.push(task);
      }
      for (const bucket of buckets) {
        bucket.sort(
          (a, b) => new Date(a.dueAt!).getTime() - new Date(b.dueAt!).getTime(),
        );
      }
    }
    return buckets;
  }, [tasks, days, showTasks]);

  if (isError) {
    return (
      <PageError
        title="Couldn't load the schedule"
        description="Try refreshing the page."
      />
    );
  }

  const windowEnd = days[BOARD_DAYS - 1]!;
  const rangeLabel = `${windowStart.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })} – ${windowEnd.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
  const onCurrentWindow = isSameLocalDay(windowStart, today);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{rangeLabel}</span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => moveWindow(addDays(windowStart, -BOARD_DAYS))}
          >
            ← Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => moveWindow(today)}
            disabled={onCurrentWindow}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => moveWindow(addDays(windowStart, BOARD_DAYS))}
          >
            Next →
          </Button>
        </div>
      </div>
      {isLoading ? (
        <PageLoading message="Loading…" />
      ) : (
        <div className="flex h-[46rem] gap-3 overflow-x-auto pb-2">
          {days.map((day, idx) => (
            <BoardColumn
              key={day.toISOString()}
              day={day}
              isToday={isSameLocalDay(day, today)}
              appointments={apptsByDay[idx] ?? []}
              tasks={tasksByDay[idx] ?? []}
              contactName={contactName}
              userName={userName}
              zoneFor={zoneFor}
              onEdit={onEdit}
              onTaskEdit={onTaskEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BoardColumn({
  day,
  isToday,
  appointments,
  tasks,
  contactName,
  userName,
  zoneFor,
  onEdit,
  onTaskEdit,
}: {
  day: Date;
  isToday: boolean;
  appointments: Appointment[];
  tasks: Task[];
  contactName: Map<string, string>;
  userName: Map<string, string>;
  zoneFor: ZoneFor;
  onEdit: (appointment: Appointment) => void;
  onTaskEdit: (task: Task) => void;
}) {
  const total = appointments.length + tasks.length;
  return (
    <div
      className={cn(
        "flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30",
        isToday && "border-primary",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between gap-2 border-b px-3 py-2",
          isToday && "bg-primary/5",
        )}
      >
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold">
            {day.toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}
          </span>
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {day.toLocaleDateString(undefined, { weekday: "long" })}
          </span>
        </div>
        <Badge variant="secondary" className="font-mono text-[10px]">
          {total}
        </Badge>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
        {total === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground">
            Nothing scheduled
          </p>
        ) : (
          <>
            {appointments.map((appointment) => (
              <button
                key={appointment.id}
                type="button"
                onClick={() => onEdit(appointment)}
                className="flex cursor-pointer flex-col gap-1.5 rounded-md border bg-card p-2.5 text-left shadow-sm transition-colors hover:border-primary/50"
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge
                    variant={STATUS_VARIANT[appointment.status]}
                    className="text-[9px] uppercase"
                  >
                    {humanize(appointment.status)}
                  </Badge>
                  <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
                    {formatClockTimeInZone(
                      appointment.scheduledStartAt,
                      zoneFor(appointment.rooftopId),
                    )}
                  </span>
                </div>
                <div className="text-sm font-semibold">
                  {humanize(appointment.appointmentType)}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    · {appointment.durationMinutes} min
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-medium text-foreground">
                    {contactName.get(appointment.contactId) ?? "—"}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    {appointment.assignedUserId
                      ? (userName.get(appointment.assignedUserId) ?? "—")
                      : "Unassigned"}
                  </span>
                </div>
              </button>
            ))}
            {tasks.map((task) => (
              <button
                key={task.id}
                type="button"
                onClick={() => onTaskEdit(task)}
                className="flex cursor-pointer flex-col gap-1 rounded-md border border-dashed bg-card p-2.5 text-left shadow-sm transition-colors hover:border-primary/50"
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="text-[9px] uppercase">
                    Task
                  </Badge>
                  <Badge variant="secondary" className="text-[9px] uppercase">
                    {humanize(task.status)}
                  </Badge>
                </div>
                <span className="text-sm font-medium leading-tight">{task.title}</span>
                {task.dueAt ? (
                  <span className="text-[11px] text-muted-foreground">
                    Due{" "}
                    {new Date(task.dueAt).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                ) : null}
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
